import { createEnv } from "@t3-oss/env-nextjs";
import { z } from "zod";

export const env = createEnv({
  /**
   * Specify your server-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars.
   */
  server: {
    AUTH_SECRET:
      process.env.NODE_ENV === "production"
        ? z.string()
        : z.string().optional(),
    NODE_ENV: z
      .enum(["development", "test", "production"])
      .default("development"),
    /** Backend API URL (server-only; never exposed to client). */
    BACKEND_API_URL: z.string().url().optional(),
    /** Backend API key (server-only; never exposed to client). */
    BACKEND_API_KEY: z.string().min(1).optional(),
    /** Public backend API key for auth and public routes; falls back to BACKEND_API_KEY. */
    PUBLIC_API_KEY: z.string().min(1).optional(),
    /** Service backend API key for server-to-server routes; falls back to BACKEND_API_KEY. */
    BACKEND_SERVICE_KEY: z.string().min(1).optional(),
    /** Logo.dev API key (server-only; used by /api/logo proxy). */
    LOGO_DEV_API_KEY: z.string().min(1).optional(),
    /** Unified.to workspace ID (server-only; used for calendar auth URLs). */
    UNIFIED_WORKSPACE_ID: z.string().min(1).optional(),
  },

  /**
   * Client-side env schema. Do NOT add backend URL or API key here - use tRPC so all backend calls go through the server.
   */
  client: {
    NEXT_PUBLIC_WEBSOCKET_SERVER_URL: z.string().url().optional(),
    /**
     * Public WebSocket base URL for the backend Browser Insight live pixel
     * viewer (`/v1/copilot/browser/sessions/{id}/view`). The browser connects
     * to the backend directly because Next.js App Router route handlers cannot
     * proxy a WebSocket upgrade. This is an infra/deployment dependency: the
     * backend WS edge (and this env var) must be provisioned separately. When
     * unset, the pixel viewer stays disabled and the canvas keeps its graceful
     * step-tree-only placeholder. It carries NO secret: the viewer WS
     * authenticates with a short-lived, single-use ticket minted server-side
     * (tRPC `mintViewTicket`), never the backend service key.
     */
    NEXT_PUBLIC_BROWSER_VIEWER_WS_URL: z.string().url().optional(),
    NEXT_PUBLIC_TRPC_DEBUG: z.string().optional(),
    /** HTTP base URL for the analytics ingestion service's POST /collect endpoint —
     *  used for both anonymous marketing-site visitors and authenticated app users
     *  (single transport; auth via optional Authorization: Bearer header). */
    NEXT_PUBLIC_ANALYTICS_COLLECT_URL: z.string().url().optional(),
    /** Debug flag: when set to "1" or "true", every sendEvent call is logged to
     *  console with the URL, payload, and response status. Off by default because
     *  analytics beacons are intentionally silent in normal operation. Turn on to
     *  diagnose missing events. Safe to leave on in staging; in production prefer
     *  enabling only in browser DevTools (`window.localStorage.setItem("...")`). */
    NEXT_PUBLIC_ANALYTICS_DEBUG: z.string().optional(),
  },

  /**
   * You can't destruct `process.env` as a regular object in the Next.js edge runtimes (e.g.
   * middlewares) or client-side so we need to destruct manually.
   */
  runtimeEnv: {
    AUTH_SECRET: process.env.AUTH_SECRET,
    NODE_ENV: process.env.NODE_ENV,
    BACKEND_API_URL: process.env.BACKEND_API_URL,
    BACKEND_API_KEY: process.env.BACKEND_API_KEY,
    PUBLIC_API_KEY: process.env.PUBLIC_API_KEY,
    BACKEND_SERVICE_KEY: process.env.BACKEND_SERVICE_KEY,
    LOGO_DEV_API_KEY:
      process.env.LOGO_DEV_API_KEY ?? process.env.NEXT_PUBLIC_LOGO_DEV_API_KEY,
    UNIFIED_WORKSPACE_ID:
      process.env.UNIFIED_WORKSPACE_ID ??
      process.env.NEXT_PUBLIC_UNIFIED_WORKSPACE_ID,
    NEXT_PUBLIC_WEBSOCKET_SERVER_URL:
      process.env.NEXT_PUBLIC_WEBSOCKET_SERVER_URL,
    NEXT_PUBLIC_BROWSER_VIEWER_WS_URL:
      process.env.NEXT_PUBLIC_BROWSER_VIEWER_WS_URL,
    NEXT_PUBLIC_TRPC_DEBUG: process.env.NEXT_PUBLIC_TRPC_DEBUG,
    NEXT_PUBLIC_ANALYTICS_COLLECT_URL:
      process.env.NEXT_PUBLIC_ANALYTICS_COLLECT_URL,
    NEXT_PUBLIC_ANALYTICS_DEBUG: process.env.NEXT_PUBLIC_ANALYTICS_DEBUG,
  },
  /**
   * Run `build` or `dev` with `SKIP_ENV_VALIDATION` to skip env validation. This is especially
   * useful for Docker builds.
   */
  skipValidation: !!process.env.SKIP_ENV_VALIDATION,
  /**
   * Makes it so that empty strings are treated as undefined. `SOME_VAR: z.string()` and
   * `SOME_VAR=''` will throw an error.
   */
  emptyStringAsUndefined: true,
});
